<template>
  <div class="subtasks-panel" @click.stop>
    <div class="sub-headrow">
      <span class="sub-title">{{ t('subtasks.toggle') }}</span>
      <span v-if="busy" class="sub-busy">{{ t('common.saving') }}</span>
    </div>

    <p v-if="error" class="sub-error">{{ error }}</p>

    <div v-if="loading" class="sub-loading">{{ t('common.loading') }}</div>

    <template v-else>
      <div class="sub-scroll">
        <table class="sub-table">
          <thead>
            <tr>
              <th class="col-check"></th>
              <th class="col-title">{{ t('subtasks.titleCol') }}</th>
              <th v-for="col in columns" :key="col.id" class="col-custom">
                <span v-if="readOnly" class="col-label-ro">{{ col.label }}</span>
                <span v-else class="col-head">
                  <input
                    class="col-label-input"
                    :value="col.label"
                    @change="renameColumn(col, $event.target.value)"
                  />
                  <button class="icon-x" :title="t('subtasks.removeColumn')" @click.stop="removeColumn(col)">×</button>
                </span>
              </th>
              <th v-if="!readOnly" class="col-add">
                <button class="addcol-btn" @click.stop="addColumn">+ {{ t('subtasks.col') }}</button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id" :class="{ 'row-done': row.is_done }">
              <td class="col-check" data-label="">
                <input type="checkbox" :checked="row.is_done" :disabled="readOnly" @change="toggleDone(row, $event.target.checked)" />
              </td>
              <td class="col-title" :data-label="t('subtasks.titleCol')">
                <span v-if="readOnly" class="cell-static">{{ row.title }}</span>
                <input v-else class="cell-input" :value="row.title" :placeholder="t('subtasks.titlePlaceholder')" @change="editTitle(row, $event.target.value)" />
              </td>
              <td v-for="col in columns" :key="col.id" class="col-custom" :data-label="col.label">
                <span v-if="readOnly" :class="['cell-static', col.type === 'status' ? 'cell-pill' : '']">{{ cell(row, col) || '—' }}</span>
                <input
                  v-else
                  :type="col.type === 'date' ? 'date' : 'text'"
                  :class="['cell-input', col.type === 'status' ? 'is-status' : '']"
                  :value="cell(row, col)"
                  @change="setCell(row, col, $event.target.value)"
                />
              </td>
              <td v-if="!readOnly" class="col-add">
                <button class="icon-x" :title="t('subtasks.removeRow')" @click.stop="removeRow(row)">×</button>
              </td>
            </tr>

            <tr v-if="rows.length === 0">
              <td :colspan="totalCols" class="sub-empty">{{ t('subtasks.empty') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!readOnly" class="sub-add">
        <input
          v-model="newTitle"
          class="cell-input add-input"
          :placeholder="t('subtasks.addPlaceholder')"
          @keyup.enter="addRow"
        />
        <button class="btn btn-sm btn-primary" :disabled="!newTitle.trim() || busy" @click.stop="addRow">
          + {{ t('subtasks.addRow') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'

export default {
  name: 'TaskSubtasks',
  props: {
    taskId:   { type: Number, required: true },
    readOnly: { type: Boolean, default: false },
  },
  setup(props) {
    const { t } = useI18n()
    const columns = ref([])
    const rows    = ref([])
    const loading = ref(true)
    const busy    = ref(false)
    const error   = ref('')
    const newTitle = ref('')

    const totalCols = computed(() => 2 + columns.value.length + (props.readOnly ? 0 : 1))

    // Only overwrite local state when the server actually returned a grid; a failed
    // request must NEVER blank the table (that looked like the panel collapsing).
    function apply(res) {
      const data = res && res.data
      if (data && Array.isArray(data.columns) && Array.isArray(data.rows)) {
        columns.value = data.columns
        rows.value    = data.rows
        return true
      }
      return false
    }

    async function run(fn) {
      error.value = ''
      busy.value = true
      try {
        const res = await fn()
        apply(res)
      } catch (e) {
        error.value = e.message || 'Action failed'
      } finally {
        busy.value = false
      }
    }

    async function load() {
      loading.value = true
      try { apply(await api.getSubtasks(props.taskId)) }
      catch (e) { error.value = e.message || 'Failed to load' }
      finally { loading.value = false }
    }

    const cell = (row, col) => {
      const v = row.values && row.values[col.id]
      return v == null ? '' : v
    }

    const addRow = () => {
      const title = newTitle.value.trim()
      if (!title) return
      return run(async () => { const r = await api.addSubtask(props.taskId, { title }); newTitle.value = ''; return r })
    }
    const editTitle  = (row, val)     => run(() => api.updateSubtask(props.taskId, row.id, { title: val }))
    const toggleDone = (row, checked) => run(() => api.updateSubtask(props.taskId, row.id, { is_done: checked ? 1 : 0 }))
    const setCell    = (row, col, val) => run(() => api.updateSubtask(props.taskId, row.id, { values: { ...(row.values || {}), [col.id]: val } }))
    const removeRow  = (row)          => run(() => api.deleteSubtask(props.taskId, row.id))
    const addColumn  = ()             => run(() => api.addSubtaskColumn(props.taskId, { label: t('subtasks.newColumn'), type: 'text' }))
    const renameColumn = (col, label) => { if (label.trim()) return run(() => api.updateSubtaskColumn(props.taskId, col.id, { label })) }
    const removeColumn = (col)        => run(() => api.deleteSubtaskColumn(props.taskId, col.id))

    onMounted(load)
    return {
      t, columns, rows, loading, busy, error, newTitle, totalCols, cell,
      addRow, editTitle, toggleDone, setCell, removeRow, addColumn, renameColumn, removeColumn,
    }
  },
}
</script>

<style scoped>
.subtasks-panel {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 var(--radius) var(--radius);
  margin: -6px 8px 6px;
  padding: 14px 16px 16px;
}
.sub-headrow { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.sub-title { font-size: 12px; font-weight: 600; color: var(--text); letter-spacing: .02em; }
.sub-busy { font-size: 11px; color: var(--muted); }
.sub-error {
  font-size: 12px; color: var(--danger);
  background: rgba(209,68,68,.1); border-radius: 8px; padding: 6px 10px; margin: 0 0 10px;
}
.sub-loading { font-size: 12px; color: var(--muted); padding: 4px 0; }

.sub-scroll { overflow-x: auto; }
/* width:auto so the grid hugs its content instead of sprawling across the page */
.sub-table { width: auto; border-collapse: separate; border-spacing: 0 6px; font-size: 13px; }
.sub-table th {
  text-align: start; font-size: 11px; font-weight: 600; color: var(--muted);
  padding: 0 8px 4px; white-space: nowrap;
}
.sub-table td { padding: 0 8px; vertical-align: middle; }
.col-check { width: 26px; text-align: center; padding-inline: 2px; }
.col-check input { width: 16px; height: 16px; cursor: pointer; accent-color: var(--accent); }
.col-title { width: 260px; }
.col-custom { width: 170px; }
.col-add { width: 32px; text-align: end; }

.col-head { display: inline-flex; align-items: center; gap: 6px; }
.col-label-ro { font-size: 11px; font-weight: 600; color: var(--muted); }
.col-label-input {
  width: 100px; background: transparent; border: none; border-bottom: 1px solid var(--border-2);
  color: var(--text); font: inherit; font-size: 12px; font-weight: 600; padding: 2px 2px 4px;
}
.col-label-input:focus { border-bottom-color: var(--accent); outline: none; }

.icon-x {
  border: none; background: none; cursor: pointer; color: var(--muted);
  font-size: 17px; line-height: 1; padding: 0 2px; border-radius: 4px;
}
.icon-x:hover { color: var(--danger); }
.addcol-btn {
  border: 1px dashed var(--border-2); background: none; cursor: pointer; color: var(--muted);
  font-size: 11px; border-radius: 7px; padding: 4px 9px; white-space: nowrap;
}
.addcol-btn:hover { color: var(--accent); border-color: var(--accent); }

.cell-input {
  width: 100%; box-sizing: border-box; background: var(--surface); color: var(--text);
  border: 1px solid var(--border); border-radius: 8px; padding: 7px 9px;
  font: inherit; font-size: 13px;
}
.cell-input::placeholder { color: var(--muted); }
.cell-input:hover { border-color: var(--border-2); }
.cell-input:focus { border-color: var(--accent); outline: none; box-shadow: 0 0 0 2px var(--accent-wash); }
.cell-input.is-status { background: var(--accent-wash); }

.cell-static { font-size: 13px; color: var(--text); }
.cell-pill { display: inline-block; background: var(--accent-wash); border-radius: 999px; padding: 3px 11px; font-size: 12px; }

.row-done .col-title .cell-static,
.row-done .col-title .cell-input { text-decoration: line-through; color: var(--muted); }

.sub-empty { text-align: center; color: var(--muted); padding: 18px 0; font-size: 12px; }

.sub-add { display: flex; gap: 8px; margin-top: 12px; align-items: center; }
.add-input { max-width: 320px; }

/* Mobile: each subtask becomes a stacked card. */
@media (max-width: 768px) {
  .sub-table, .sub-table tbody, .sub-table tr, .sub-table td { display: block; width: 100%; }
  .sub-table thead { display: none; }
  .sub-table { border-spacing: 0; }
  .sub-table tr {
    border: 1px solid var(--border); border-radius: var(--radius);
    background: var(--surface); padding: 10px 12px; margin-bottom: 10px;
  }
  .sub-table td { padding: 5px 0; display: flex; align-items: center; gap: 10px; }
  .sub-table td[data-label]:not(.col-check)::before {
    content: attr(data-label); flex: 0 0 84px;
    font-size: 11px; font-weight: 600; color: var(--muted);
  }
  .col-check { justify-content: flex-start; }
  .col-add { justify-content: flex-end; }
  .add-input { max-width: none; }
}
</style>
